import { Button, CircularProgress, Stack } from '@mui/material'
import PixIcon from '@mui/icons-material/Pix'
import ContentDialog from '../../../../../../components/Dialogs/ContentDialog'
import { useCallback, useState } from 'react'
import { useAdminUserContext } from '../../../../../../core/hooks/useAdminUserContext'
import AdminController from '../../../../../../core/controllers/AdminController'
import {
  AdminTransferCalculatedValue,
  AdminTransferResponse,
} from '../../../../../../core/types/Admin'
import { TConfirmDialogButton } from '../../../../../../components/Dialogs/DialogActions/DialogActionButton'
import UserSettingExtractTransferQrCode from './UserSettingExtractTransferQrCode'
import UserSettingExtractTransferData from './UserSettingExtractTransferData'
import NumberFunctions from '../../../../../../core/functions/NumberFunctions'

interface UserSettingExtractTransferButtonProps {
  onRefresh?: () => void
}

const UserSettingExtractTransferButton = ({
  onRefresh,
}: UserSettingExtractTransferButtonProps) => {
  const { tenantSettings } = useAdminUserContext()

  const [openDialog, setOpenDialog] = useState(false)
  const [sending, setSending] = useState(false)
  const [value, setValue] = useState('0')
  const [error, setError] = useState('')
  const [response, setResponse] = useState<AdminTransferResponse | null>(null)
  const [data, setData] = useState<AdminTransferCalculatedValue | null>(null)
  const [loading, setLoading] = useState(true)

  const handleOpenDialog = () => {
    getData()
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setValue('0')
    setError('')
    setResponse(null)
    setOpenDialog(false)
    if (onRefresh) {
      onRefresh()
    }
  }

  const getData = useCallback(async () => {
    if (tenantSettings?.tenantId) {
      setLoading(true)
      try {
        const response = await AdminController.getCalculatedValue({
          tenantId: tenantSettings.tenantId,
        })

        if (response.success) {
          setData(response.data ?? null)
        } else {
          setError(response.error)
        }
      } finally {
        setLoading(false)
      }
    }
  }, [tenantSettings?.tenantId])

  const isErrorWithdrawValue = () => {
    const onlyValue =
      typeof value === 'string' ? value.replaceAll('R$ ', '') : value
    const amount = parseFloat(onlyValue ?? '0')
    return amount > 999999999
  }

  const getAmountValue = () => {
    if (isErrorWithdrawValue()) {
      return 0
    }

    const onlyValue =
      typeof value === 'string' ? value.replaceAll('R$ ', '') : value
    const amount = parseFloat(onlyValue ?? '0')
    const fees = NumberFunctions.roundTo(amount * 0.01)
    return amount < 5 ? 0 : amount + fees
  }

  const handleSubmit = async () => {
    if (isErrorWithdrawValue()) {
      setError('Valor inválido')
      return
    }

    const amount = getAmountValue()
    if (amount < 5) {
      setError('Valor mínimo de R$ 5,00')
      return
    }

    if (!tenantSettings?.tenantEmail) {
      setError('E-mail do usuário não encontrado')
      return
    }

    try {
      setSending(true)
      setError('')

      const response = await AdminController.transfer({
        data: { email: tenantSettings.tenantEmail, value: amount },
      })

      if (!response.success) {
        setError(response.error)
        setResponse(null)
        return false
      } else {
        if (response.data?.pixQrCode === null) {
          setError('Erro ao gerar QR Code')
          setResponse(null)
          return false
        }
        setResponse(response.data ?? null)
      }
    } finally {
      setSending(false)
    }

    return true
  }

  const buttons: TConfirmDialogButton[] =
    response === null
      ? [
          {
            title: 'Cancelar',
            type: 'negative',
            onClick: handleCloseDialog,
          },
          {
            title: 'Gerar QR Code',
            type: 'positive',
            onClick: handleSubmit,
            isDisabled: sending,
          },
        ]
      : [
          {
            title: 'Voltar',
            type: 'negative',
            onClick: () => setResponse(null),
          },
          {
            title: 'Fechar',
            type: 'positive',
            onClick: handleCloseDialog,
            isDisabled: sending,
          },
        ]

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<PixIcon />}
        sx={{ textTransform: 'none' }}
        onClick={handleOpenDialog}
        disabled={sending}
      >
        Transferir valor
      </Button>
      <ContentDialog
        maxWidth="xs"
        title="Realizar transferência"
        open={openDialog}
        onClose={handleCloseDialog}
        buttons={buttons}
      >
        {loading && (
          <Stack alignItems="center" justifyContent="center" minHeight={200}>
            <CircularProgress />
          </Stack>
        )}
        {!loading && response === null && (
          <UserSettingExtractTransferData
            data={data}
            sending={sending}
            value={value}
            setValue={setValue}
            error={error}
            setError={setError}
            isErrorWithdrawValue={isErrorWithdrawValue}
            getAmountValue={getAmountValue}
          />
        )}
        {response !== null && (
          <UserSettingExtractTransferQrCode data={response} />
        )}
      </ContentDialog>
    </>
  )
}

export default UserSettingExtractTransferButton
